/**
 * TEMPORARY — Desktop layout concept demo.
 * Route: /desktop-demo (public, no auth, no real data)
 * Delete this file and its route in App.tsx once reviewed.
 *
 * Shows: sidebar nav + two-column home layout (timeline left, charts right)
 * Everything is static mock data — no contexts required.
 */

import { useState } from 'react';
import {
  Home, Lightbulb, Star, Gamepad2, Heart,
  Settings, Plus, ChevronRight,
  Moon, Milk,
  TrendingUp,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  AreaChart, Area,
} from 'recharts';

// ── Mock data ─────────────────────────────────────────────────────────────────

const MOCK_BABY = { name: 'Arya', age: '4 months 12 days', avatarEmoji: '👶' };

const MOCK_STATS = [
  { type: 'feed',     icon: '🍼', label: 'Last Feed',    value: '2h 15m ago',  sub: '120ml bottle',        color: 'bg-orange-50',  dot: 'bg-orange-300' },
  { type: 'sleep',    icon: '😴', label: 'Last Sleep',   value: '3h 40m',      sub: 'Ended at 9:30 AM',    color: 'bg-violet-50',  dot: 'bg-violet-300' },
  { type: 'diaper',   icon: '🧷', label: 'Diapers Today', value: '4',          sub: '2 wet · 2 soiled',    color: 'bg-emerald-50', dot: 'bg-emerald-300' },
  { type: 'growth',   icon: '📊', label: 'Weight',       value: '6.2 kg',      sub: 'Logged 3 days ago',   color: 'bg-pink-50',    dot: 'bg-pink-300' },
  { type: 'medicine', icon: '💊', label: 'Medicine',     value: '1 / 2 done',  sub: 'Vitamin D · evening due', color: 'bg-teal-50', dot: 'bg-teal-300' },
];

const MOCK_TIMELINE = [
  { time: '7:10 AM',  kind: 'feed',   icon: '🍼', title: 'Bottle Feed',    detail: '120ml',      color: 'bg-orange-100', dot: 'bg-orange-400' },
  { time: '8:45 AM',  kind: 'sleep',  icon: '😴', title: 'Nap',            detail: '1h 10m',     color: 'bg-violet-100', dot: 'bg-violet-400' },
  { time: '10:20 AM', kind: 'diaper', icon: '💧', title: 'Diaper Change',  detail: 'Wet',        color: 'bg-emerald-100',dot: 'bg-emerald-400' },
  { time: '10:30 AM', kind: 'feed',   icon: '🤱', title: 'Breastfeed',     detail: '22 min',     color: 'bg-orange-100', dot: 'bg-orange-400' },
  { time: '11:50 AM', kind: 'diaper', icon: '💩', title: 'Diaper Change',  detail: 'Soiled',     color: 'bg-emerald-100',dot: 'bg-emerald-400' },
  { time: '12:15 PM', kind: 'sleep',  icon: '😴', title: 'Nap',            detail: '45 min',     color: 'bg-violet-100', dot: 'bg-violet-400' },
  { time: '1:30 PM',  kind: 'feed',   icon: '🍼', title: 'Bottle Feed',    detail: '90ml',       color: 'bg-orange-100', dot: 'bg-orange-400' },
  { time: '2:45 PM',  kind: 'diaper', icon: '💧💩', title: 'Diaper Change', detail: 'Wet & Soiled', color: 'bg-emerald-100',dot: 'bg-emerald-400' },
];

const FEED_DATA = [
  { day: 'Mon', ml: 480 },
  { day: 'Tue', ml: 510 },
  { day: 'Wed', ml: 450 },
  { day: 'Thu', ml: 540 },
  { day: 'Fri', ml: 490 },
  { day: 'Sat', ml: 520 },
  { day: 'Sun', ml: 390 },
];

const SLEEP_DATA = [
  { day: 'Mon', hrs: 13.5 },
  { day: 'Tue', hrs: 14.2 },
  { day: 'Wed', hrs: 12.8 },
  { day: 'Thu', hrs: 14.0 },
  { day: 'Fri', hrs: 13.1 },
  { day: 'Sat', hrs: 15.0 },
  { day: 'Sun', hrs: 11.5 },
];

const NAV_ITEMS = [
  { id: 'today',      label: 'Today',      Icon: Home },
  { id: 'knowledge',  label: 'Knowledge',  Icon: Lightbulb },
  { id: 'milestones', label: 'Milestones', Icon: Star },
  { id: 'activities', label: 'Activities', Icon: Gamepad2 },
  { id: 'health',     label: 'Health',     Icon: Heart },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const Sidebar = ({ active, setActive }: { active: string; setActive: (id: string) => void }) => (
  <aside
    className="fixed top-0 left-0 h-full flex flex-col border-r border-gray-100"
    style={{ width: 220, background: '#FAFAF9' }}
  >
    {/* Logo area */}
    <div className="px-5 pt-6 pb-4 flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-pink-200 to-violet-200 flex items-center justify-center text-sm">👶</div>
      <span className="text-sm font-semibold text-gray-700 tracking-wide">numaa</span>
    </div>

    {/* Baby switcher pill */}
    <div className="mx-3 mb-5 px-3 py-2.5 rounded-xl bg-white border border-gray-100 flex items-center gap-2.5 shadow-sm cursor-pointer hover:bg-gray-50 transition-colors">
      <span className="text-lg">{MOCK_BABY.avatarEmoji}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 leading-none">{MOCK_BABY.name}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{MOCK_BABY.age}</p>
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
    </div>

    {/* Nav items */}
    <nav className="flex-1 px-2 space-y-0.5">
      {NAV_ITEMS.map(({ id, label, Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            onClick={() => setActive(id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive
                ? 'bg-violet-50 text-violet-700'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-violet-600' : ''}`} />
            {label}
          </button>
        );
      })}
    </nav>

    {/* Bottom actions */}
    <div className="px-2 pb-5 space-y-0.5 border-t border-gray-100 pt-3 mt-2">
      <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
        <Settings className="w-4 h-4" />
        Settings
      </button>
    </div>
  </aside>
);

const StatGrid = ({ onLog }: { onLog: (type: string) => void }) => {
  // ceil(n/2) columns so tiles fill available width equally in 2 rows
  const cols = Math.ceil(MOCK_STATS.length / 2);
  return (
  <div
    className="grid gap-3 overflow-x-auto pb-1"
    style={{
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      gridTemplateRows: 'repeat(2, auto)',
      gridAutoFlow: 'column',
    }}
  >
    {MOCK_STATS.map((s) => (
      <div
        key={s.type}
        className="bg-white rounded-2xl px-4 py-3.5 flex items-center gap-3 shadow-sm border border-gray-50 cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className={`${s.color} w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0`}>
          <span className="text-lg leading-none">{s.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider leading-none mb-1">{s.label}</p>
          <p className="text-base font-bold text-gray-900 leading-tight">{s.value}</p>
          <p className="text-[11px] text-gray-400 mt-0.5 truncate">{s.sub}</p>
        </div>
        <button
          onClick={() => onLog(s.type)}
          className="w-7 h-7 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <Plus className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </div>
    ))}
  </div>
  );
};

const Timeline = () => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-50 overflow-hidden flex flex-col" style={{ minHeight: 420 }}>
    <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
      <div>
        <h2 className="text-sm font-semibold text-gray-800">Today's Timeline</h2>
        <p className="text-xs text-gray-400 mt-0.5">Sunday, 13 April · 8 entries</p>
      </div>
      <button className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 bg-violet-50 px-3 py-1.5 rounded-full hover:bg-violet-100 transition-colors">
        <Plus className="w-3 h-3" />
        Add
      </button>
    </div>

    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
      {MOCK_TIMELINE.map((ev, idx) => {
        const isLast = idx === MOCK_TIMELINE.length - 1;
        return (
          <div key={idx} className="flex gap-3 items-start">
            {/* Time */}
            <div className="w-16 pt-2.5 flex-shrink-0 text-right">
              <span className="text-[11px] text-gray-400">{ev.time}</span>
            </div>
            {/* Spine */}
            <div className="flex flex-col items-center w-4 flex-shrink-0">
              <div className={`w-2 h-2 rounded-full mt-2.5 flex-shrink-0 ${ev.dot}`} />
              {!isLast && <div className="w-px flex-1 bg-gray-100 mt-1 min-h-[20px]" />}
            </div>
            {/* Card */}
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-2.5 bg-gray-50/80 rounded-xl py-2 px-2.5">
                <span className="text-sm">{ev.icon}</span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-800">{ev.title}</p>
                  <p className="text-[10px] text-gray-400">{ev.detail}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const ChartPanel = () => (
  <div className="space-y-3">
    {/* Feed chart */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-50 px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <Milk className="w-4 h-4 text-orange-400" />
        <h3 className="text-sm font-semibold text-gray-700">Feed volume · this week</h3>
      </div>
      <ResponsiveContainer width="100%" height={110}>
        <BarChart data={FEED_DATA} barSize={14}>
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            formatter={(v) => [`${v}ml`, 'Total']}
          />
          <Bar dataKey="ml" fill="#FDBA74" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>

    {/* Sleep chart */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-50 px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <Moon className="w-4 h-4 text-violet-400" />
        <h3 className="text-sm font-semibold text-gray-700">Sleep · this week</h3>
      </div>
      <ResponsiveContainer width="100%" height={110}>
        <AreaChart data={SLEEP_DATA}>
          <defs>
            <linearGradient id="sleepGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#A78BFA" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
          <YAxis hide domain={[10, 16]} />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            formatter={(v) => [`${v}h`, 'Sleep']}
          />
          <Area type="monotone" dataKey="hrs" stroke="#A78BFA" strokeWidth={2} fill="url(#sleepGrad)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>

    {/* Quick stats strip */}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-50 px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-400" />
        <h3 className="text-sm font-semibold text-gray-700">Week at a glance</h3>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Avg feeds/day', value: '6.2', icon: '🍼' },
          { label: 'Avg sleep/day', value: '13.4h', icon: '😴' },
          { label: 'Avg diapers/day', value: '5.8', icon: '🧷' },
        ].map(s => (
          <div key={s.label} className="text-center">
            <div className="text-xl mb-1">{s.icon}</div>
            <p className="text-sm font-bold text-gray-900">{s.value}</p>
            <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ── Page shell ────────────────────────────────────────────────────────────────

const TopBar = ({ activeTab }: { activeTab: string }) => (
  <header
    className="h-14 border-b border-gray-100 flex items-center justify-between px-8 flex-shrink-0"
    style={{ background: '#FFFAF8' }}
  >
    <div>
      <h1 className="text-base font-semibold text-gray-900 capitalize">{activeTab === 'today' ? 'Today' : activeTab}</h1>
      <p className="text-[11px] text-gray-400">Sunday, 13 April 2025</p>
    </div>
    <div className="flex items-center gap-3">
      <button className="flex items-center gap-2 text-sm font-semibold text-white bg-violet-500 hover:bg-violet-600 px-4 py-2 rounded-full transition-colors">
        <Plus className="w-4 h-4" />
        Log entry
      </button>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-200 to-violet-200 flex items-center justify-center text-sm cursor-pointer">
        👶
      </div>
    </div>
  </header>
);

// ── Placeholder screens for non-Today tabs ────────────────────────────────────

const PlaceholderScreen = ({ label, icon }: { label: string; icon: string }) => (
  <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-300">
    <span className="text-5xl">{icon}</span>
    <p className="text-lg font-medium">{label} — demo placeholder</p>
    <p className="text-sm">Switch to Today to see the layout concept.</p>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const DesktopDemo = () => {
  const [activeTab, setActiveTab] = useState('today');
  const [logToast, setLogToast] = useState<string | null>(null);

  const handleLog = (type: string) => {
    setLogToast(`+ ${type} entry (demo — no real save)`);
    setTimeout(() => setLogToast(null), 2000);
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#F5F0EC', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Sidebar */}
      <Sidebar active={activeTab} setActive={setActiveTab} />

      {/* Main */}
      <div className="flex flex-col flex-1 overflow-hidden" style={{ marginLeft: 220 }}>
        {/* Top bar */}
        <TopBar activeTab={activeTab} />

        {/* Content */}
        <main className="flex-1 overflow-y-auto" style={{ background: '#F5F0EC' }}>
          {activeTab === 'today' ? (
            <div className="px-8 py-6 max-w-[1100px]">

              {/* Stat grid */}
              <StatGrid onLog={handleLog} />

              {/* Two-column: Timeline + Charts */}
              <div className="mt-5 grid gap-4" style={{ gridTemplateColumns: '1fr 340px' }}>
                <Timeline />
                <ChartPanel />
              </div>

            </div>
          ) : activeTab === 'knowledge' ? (
            <PlaceholderScreen label="Knowledge" icon="💡" />
          ) : activeTab === 'milestones' ? (
            <PlaceholderScreen label="Milestones" icon="⭐" />
          ) : activeTab === 'activities' ? (
            <PlaceholderScreen label="Activities" icon="🎮" />
          ) : (
            <PlaceholderScreen label="Health" icon="❤️" />
          )}
        </main>
      </div>

      {/* Demo label */}
      <div
        className="fixed top-3 right-3 text-[10px] font-mono text-gray-400 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-lg border border-gray-200"
      >
        DEMO — /desktop-demo · delete after review
      </div>

      {/* Log toast */}
      {logToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-medium px-4 py-2.5 rounded-full shadow-lg">
          {logToast}
        </div>
      )}
    </div>
  );
};

export default DesktopDemo;
